import React from 'react';
import { Link } from 'react-router-dom';
import { Gift, Heart, ArrowRight, Star } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { SEO } from '../../components/common/SEO';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';

const giftGuides = [
  {
    title: 'Gifts for Him',
    description: 'Perfect gifts for the men in your life',
    category: 'men',
    items: 25,
    featured: true
  },
  {
    title: 'Gifts for Her',
    description: 'Thoughtful gifts for the women you love',
    category: 'women',
    items: 30,
    featured: true
  },
  {
    title: 'Tech Gifts',
    description: 'Latest gadgets and tech accessories',
    category: 'tech',
    items: 20,
    featured: false
  },
  {
    title: 'Gifts Under $25',
    description: 'Budget-friendly options that still impress',
    category: 'budget',
    items: 35,
    featured: false
  },
  {
    title: 'Gifts for Kids',
    description: 'Fun and educational gifts for children',
    category: 'kids',
    items: 28,
    featured: false
  },
  {
    title: 'Luxury Gifts',
    description: 'High-end gifts for special occasions',
    category: 'luxury',
    items: 15,
    featured: false
  }
];

export const GiftGuidesPage: React.FC = () => {
  const canonicalUrl = useCanonicalUrl();

  return (
    <>
      <SEO
        title="Gift Guides - Perfect Gift Ideas for Everyone"
        description="Discover curated gift guides for every person and occasion. From budget-friendly options to luxury gifts, find the perfect present for everyone on your list."
        keywords={['gift guides', 'gift ideas', 'christmas gifts', 'birthday gifts', 'holiday gifts', 'gifts for him', 'gifts for her']}
        url={canonicalUrl}
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Gift Guides
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Discover the perfect gifts for everyone on your list. Our curated guides 
              feature handpicked items for every budget, age, and interest.
            </p>
            <Link to="/dashboard">
              <Button size="lg">
                Create Your Wishlist
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

          {/* Featured Guides */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Featured Gift Guides</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {giftGuides.filter(guide => guide.featured).map((guide, index) => (
                <Link key={index} to={`/gift-guides/${guide.category}`}>
                  <Card hover className="relative">
                    <div className="absolute top-4 right-4">
                      <Star className="h-5 w-5 text-yellow-500 fill-current" />
                    </div>
                    <Gift className="h-12 w-12 text-pink-500 mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{guide.title}</h3>
                    <p className="text-gray-600 mb-4">{guide.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{guide.items} gift ideas</span>
                      <Button variant="outline" size="sm">
                        View Guide
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </section>

          {/* All Guides */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">All Gift Guides</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {giftGuides.map((guide, index) => (
                <Link key={index} to={`/gift-guides/${guide.category}`}>
                  <Card hover>
                    <Gift className="h-8 w-8 text-purple-500 mb-3" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{guide.title}</h3>
                    <p className="text-gray-600 mb-3">{guide.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{guide.items} items</span>
                      <Button variant="outline" size="sm">
                        View
                      </Button>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </section>

          {/* Categories */}
          <section className="mb-12">
            <Card className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Browse by Category</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['Electronics', 'Fashion', 'Home & Garden', 'Sports', 'Books', 'Beauty', 'Food & Drink', 'Travel'].map((category) => (
                  <Link key={category} to={`/gift-guides/category/${category.toLowerCase()}`}>
                    <Card hover className="text-center p-4">
                      <h3 className="font-semibold text-gray-900">{category}</h3>
                    </Card>
                  </Link>
                ))}
              </div>
            </Card>
          </section>

          {/* Tips */}
          <section className="mb-12">
            <Card className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Gift-Giving Tips</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Consider the Recipient</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Think about their hobbies and interests</li>
                    <li>• Consider their lifestyle and needs</li>
                    <li>• Remember past conversations about wants</li>
                    <li>• Check their social media for clues</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Budget Wisely</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Set a realistic spending limit</li>
                    <li>• Look for sales and discounts</li>
                    <li>• Consider group gifts for expensive items</li>
                    <li>• Remember: thoughtfulness matters more than price</li>
                  </ul>
                </div>
              </div>
            </Card>
          </section>

          {/* CTA */}
          <section className="text-center">
            <Card className="p-8 bg-white">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Ready to Start Shopping?
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Create your wishlist and start planning the perfect gifts today.
              </p>
              <Link to="/dashboard">
                <Button size="lg">
                  <Heart className="h-5 w-5 mr-2" />
                  Create Wishlist
                </Button>
              </Link>
            </Card>
          </section>
        </div>
      </div>
    </>
  );
};
